import { diInject } from '@di/di';
import { EventEmitter } from '@studio/utils/event-emitter';
import { getInAnimationDuration, getOutAnimationDuration } from '../animation.utils';
import { isSelectionVisibleAtTime } from '../nodes/helpers';
import { animatorEngine, AnimatorEngine } from './animator.engine';
import { FpsMonitor } from './fps-monitor';
export class Animator extends EventEmitter {
    /**
     * Duration of creative in seconds
     */
    get duration() {
        return this._duration;
    }
    constructor({ useGlobalEngine } = { useGlobalEngine: true }) {
        super();
        /**
         * Current time in seconds.
         */
        this.time = 0;
        /**
         * If the creative is playing or not.
         */
        this.isPlaying = false;
        /**
         * Loop currently playing. First loop has index 1.
         */
        this.loop = 1;
        this._destroyed = false;
        this._duration = 0;
        this._startTimestamp = 0;
        this._lastTime = 0;
        this._speed = 1;
        this._elementsAnimationState = new Map();
        /**
         * Queue of events that has to occur after the current tick is completed.
         * Required by e.g widgets to ensure that seeking happens after next (loop).
         */
        this._eventQueue = [];
        this._renderer = diInject(3 /* Token.RENDERER */);
        this._feedStore = diInject(6 /* Token.FEED_STORE */, { optional: true });
        this._creativeConfig = diInject(2 /* Token.CREATIVE_CONFIG */);
        this._tick = (t) => {
            if (!this._fpsMonitor.canDraw_m(t)) {
                return;
            }
            this._fpsMonitor.markDrawStart_m(t);
            /**
             * We need to force-cancel if isPlaying is false as it seems
             * that if animator is paused from the Widget API then it occurs on a
             * different tick causing everything to go haywire
             */
            if (this._destroyed || !this.isPlaying) {
                return;
            }
            if (!this._startTimestamp) {
                this._startTimestamp = t;
            }
            const stopTime = this._getStopTime();
            const loops = this.creative.loops;
            const timeDiff = (t - this._startTimestamp) * this._speed;
            const timeInSeconds = (this._lastTime + timeDiff) / 1000;
            if (!this._infinite() &&
                this.loop >= loops &&
                timeInSeconds >= stopTime &&
                this._creativeConfig.environment !== 5 /* AdEnvironment.VideoGenerator */ &&
                this._creativeConfig.environment !== 7 /* AdEnvironment.AudioGenerator */) {
                this.stop();
                return;
            }
            if ((this._creativeConfig.environment === 5 /* AdEnvironment.VideoGenerator */ ||
                this._creativeConfig.environment === 7 /* AdEnvironment.AudioGenerator */) &&
                timeInSeconds >= this.duration) {
                this.setTime_m(this.duration);
                this.pause();
                return;
            }
            if (timeInSeconds < this.duration) {
                this.setTime_m(timeInSeconds);
            }
            else if (timeInSeconds >= this.duration) {
                this.setTime_m(this.duration);
                this.next();
            }
            while (this._eventQueue.length) {
                const event = this._eventQueue.pop();
                event.fn(...event.value);
            }
            this.emit('tick', this.time);
            this._fpsMonitor.markDrawFinish_m(this.time);
        };
        this._engine = useGlobalEngine ? animatorEngine : new AnimatorEngine();
        this._engine.registerAnimator_m(this);
        this.creative = this._renderer.creativeDocument;
        this.time = this._getStartTime();
        this._duration = getCreativeAnimationDuration(this.creative);
        const shouldLimitFps = this._creativeConfig.environment !== 5 /* AdEnvironment.VideoGenerator */ &&
            this._creativeConfig.environment !== 1 /* AdEnvironment.App */;
        this._fpsMonitor = new FpsMonitor({
            minFps: 10,
            durationInSeconds: this._duration,
            shouldLimitFps
        });
    }
    /**
     * Play the creative if not already playing
     */
    play() {
        if (!this.isPlaying && this.creative.elements.length > 0) {
            this._duration = getCreativeAnimationDuration(this.creative);
            this._lastTime = this.time * 1000;
            this.isPlaying = true;
            this._renderer.setPlaying_m(true);
            this._engine.start_m();
            this.emit('isPlaying', true);
            this.emit('play', this.time);
        }
        return this;
    }
    /**
     * Toggle between play and pause
     */
    toggle() {
        this.isPlaying ? this.pause() : this.play();
        return this;
    }
    /**
     * Pause the creative if the creative is playing
     */
    pause() {
        if (this.isPlaying) {
            this.isPlaying = false;
            this._engine.notifyAnimatorPaused_m();
            this._lastTime = 0;
            this._startTimestamp = 0;
            this._renderer.setPlaying_m(false);
            this.emit('isPlaying', false);
            this.emit('pause', this.time);
        }
        return this;
    }
    /**
     * Stop the creative. Except from pausing the creative it will also seek to the stopTime.
     * Defaults at duration - 1s
     */
    stop() {
        // Don't allow to stop the creative when in VideoGenerator or AudioGenerator env.
        if (this._creativeConfig.environment !== 5 /* AdEnvironment.VideoGenerator */ &&
            this._creativeConfig.environment !== 7 /* AdEnvironment.AudioGenerator */) {
            const stopTime = this._getStopTime();
            this.pause();
            this.setTime_m(stopTime);
        }
        this.emit('stop', this.time);
    }
    /**
     * Seek to a certain point in time in seconds.
     */
    seek(time) {
        time = Math.max(time, 0) || 0;
        if (this.time !== time) {
            this._lastTime = time * 1000;
            this._startTimestamp = 0;
            this.setTime_m(time);
            this.emit('seek', this.time);
        }
        return this;
    }
    /**
     * Seek to a certain time when the element is visible.
     */
    seekToElement_m(elementId) {
        this.pause();
        const element = this.creative.elements.find(e => e.id === elementId);
        if (element) {
            const elementVisibleTime = element.time + element.duration / 2;
            this.seek(elementVisibleTime);
        }
        else {
            console.warn(`Tried to seek to element[${elementId}], but element does not exist`);
        }
        return this;
    }
    /**
     * Jump to next loop.
     * Will play from the beginning of the loop
     */
    next(replay = true, offset) {
        const loop = this.loop + (offset || 1);
        let exceededLimit = false;
        if (this._feedStore) {
            exceededLimit = this._feedStore.loop < 1;
            if (exceededLimit) {
                this._feedStore.setFeedLoop(offset || 1);
            }
        }
        this.setLoop(loop, replay, !exceededLimit);
        return this;
    }
    /**
     * Jump to previous loop.
     * Will play from the beginning of the loop
     */
    prev(replay = true, offset, exceedLimit) {
        const loop = this.loop - (offset || 1);
        if (this._feedStore) {
            this._feedStore.setFeedLoop(this._feedStore.loop - (offset || 1), exceedLimit);
        }
        this.setLoop(loop, replay, false);
        return this;
    }
    /**
     * Replay current loop from beginning.
     */
    replay() {
        this.seek(0);
        this.emit('replay', this.loop);
        return this;
    }
    /**
     * Restart creative. Loops and time will be reset.
     */
    restart() {
        if (this._creativeConfig.environment === 5 /* AdEnvironment.VideoGenerator */ ||
            this._creativeConfig.environment === 7 /* AdEnvironment.AudioGenerator */) {
            // Cannot restart Animator when in VideoGenerator or AudioGenerator env
            return this;
        }
        if (this._feedStore) {
            this._feedStore.resetIndexState();
            this._feedStore.skipNextIndexUpdate();
        }
        this.setLoop(1);
        this.play();
        this.emit('restart', this.loop);
        return this;
    }
    /**
     * Updates values on DOM
     */
    render_m(time, forceUpdate) {
        this._updateElementsState();
        this._renderer.setAllViewElementsValues_m(typeof time === 'number' ? time : this.time, forceUpdate);
    }
    useCreative_m(creative) {
        this.creative = creative;
    }
    setTime_m(time, forceUpdate) {
        this.time = time;
        this.render_m(this.time, forceUpdate);
    }
    setLoop(loop, replay = true, setFeedLoop = true) {
        if (this._feedStore) {
            if (setFeedLoop) {
                this._feedStore.setFeedLoop(this._feedStore.loop < 0 ? this._feedStore.loop : loop);
            }
            this._feedStore.updateElementsCurrentIndex();
        }
        if (replay) {
            this._lastTime = 0;
            this._startTimestamp = 0;
            this.setTime_m(0, /* forceUpdate */ true);
        }
        else {
            this.render_m(this.time, /* forceUpdate */ true);
        }
        this.loop = Math.max(1, loop);
        if (this.loop === 1) {
            this.time = this._getStartTime();
        }
        if (this._creativeConfig.environment !== 5 /* AdEnvironment.VideoGenerator */ &&
            this._creativeConfig.environment !== 7 /* AdEnvironment.AudioGenerator */) {
            this.emit('loop', this.loop);
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addToQueue(fn, value = []) {
        if (this.isPlaying) {
            this._eventQueue.push({ fn: fn.bind(this), value });
        }
        else {
            fn.bind(this)(...value);
        }
    }
    updateCustomFPSInterval(intervalInMs) {
        this._fpsMonitor.setMaxFpsInterval_m(intervalInMs);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addToQueueFromWidget(fn, value) {
        if (this._creativeConfig.environment === 5 /* AdEnvironment.VideoGenerator */ ||
            this._creativeConfig.environment === 7 /* AdEnvironment.AudioGenerator */) {
            return;
        }
        this.addToQueue(fn, value);
    }
    /**
     * Keeps track of the current state of all elements and emits events based on what changes
     * Tracks what kind of animation that latest ran, e.g the "in" or "out" part of the In transition
     */
    _updateElementsState() {
        if (!this.isPlaying) {
            return;
        }
        for (let i = 0; i < this.creative.elements.length; i++) {
            const element = this.creative.elements[i];
            const animations = element.animations;
            if (!this._elementsAnimationState.has(element.id)) {
                this._elementsAnimationState.set(element.id, { visible: false });
            }
            const currentState = this._elementsAnimationState.get(element.id);
            let animationState = 'none';
            const inDuration = getInAnimationDuration(animations);
            const outDuration = getOutAnimationDuration(animations);
            if (inDuration) {
                if (this.time > element.time && this.time < inDuration + element.time) {
                    animationState = 'inTransitionIn';
                }
                else if (currentState.animationState === 'inTransitionIn' &&
                    (this.time <= element.time || this.time >= inDuration + element.time)) {
                    animationState = 'inTransitionOut';
                }
            }
            if (outDuration) {
                const outDurationStart = element.time + element.duration - outDuration;
                if (this.time > outDurationStart && this.time < element.time + element.duration) {
                    animationState = 'outTransitionIn';
                }
                else if (currentState.animationState === 'outTransitionIn' &&
                    (this.time < outDurationStart || this.time >= element.duration + element.time)) {
                    animationState = 'outTransitionOut';
                }
            }
            if (isSelectionVisibleAtTime(element, this.time)) {
                this._elementsAnimationState.set(element.id, {
                    visible: true,
                    animationState: animationState
                });
            }
            else {
                this._elementsAnimationState.set(element.id, {
                    visible: false,
                    animationState: animationState
                });
            }
            const isAtStartOfElement = this.time === 0 && element.time === 0;
            const isAtEndOfElement = this.time === element.duration + element.time;
            const state = this._elementsAnimationState.get(element.id);
            if (currentState.visible !== state.visible || isAtStartOfElement || isAtEndOfElement) {
                if (((state.visible && !isAtEndOfElement) || isAtStartOfElement) &&
                    currentState.animationState !== 'animationStarted') {
                    state.animationState = 'animationStarted';
                    this.emit('animation_start', element);
                }
                else if (((!state.visible && !isAtStartOfElement) || isAtEndOfElement) &&
                    currentState.animationState !== 'animationEnded' &&
                    (currentState.animationState !== 'none' || outDuration === 0)) {
                    state.animationState = 'animationEnded';
                    this.emit('animation_end', element);
                }
            }
            if (currentState.animationState !==
                this._elementsAnimationState.get(element.id).animationState) {
                switch (this._elementsAnimationState.get(element.id).animationState) {
                    case 'inTransitionOut':
                        this.emit('in_transition_end', element);
                        break;
                    case 'outTransitionIn':
                        this.emit('out_transition_start', element);
                        break;
                }
            }
        }
    }
    tick(t) {
        this._tick(t);
    }
    _getStartTime() {
        return typeof this.creative.startTime === 'number' ? this.creative.startTime : 0;
    }
    _getStopTime() {
        return typeof this.creative.stopTime === 'number' ? this.creative.stopTime : this.duration - 1;
    }
    _infinite() {
        if (this._creativeConfig.appView === "dv" /* AppView.DesignView */) {
            return true;
        }
        return !this.creative.loops;
    }
    destroy() {
        if (this._destroyed) {
            return;
        }
        this.stop();
        this._destroyed = true;
        this._engine.unregisterAnimator_m(this);
        this._engine = undefined;
        this.clearEvents();
    }
}
export function getCreativeAnimationDuration(creative) {
    let latestAnimationEnd = 0;
    for (const element of creative.elements) {
        if (element.time + element.duration > latestAnimationEnd) {
            latestAnimationEnd = element.time + element.duration;
        }
    }
    return latestAnimationEnd;
}
